import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, AlertCircle, Wrench, ChevronRight, Server } from "lucide-react";

const OVERALL_LABEL = {
  operational: { label: "Tutti i sistemi operativi", Icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50", ring: "ring-emerald-200" },
  maintenance: { label: "Manutenzione in corso", Icon: Wrench, color: "text-orange-700", bg: "bg-orange-50", ring: "ring-orange-200" },
  degraded:    { label: "Servizio con avvisi", Icon: AlertTriangle, color: "text-amber-800", bg: "bg-amber-50", ring: "ring-amber-200" },
  outage:      { label: "Disservizio in corso", Icon: AlertCircle, color: "text-rose-700", bg: "bg-rose-50", ring: "ring-rose-200" },
};

const SEV_DOT = {
  info: "bg-blue-500", maintenance: "bg-orange-500", warning: "bg-amber-500", critical: "bg-rose-600",
};

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    axios.get(`${base}/api/status/page`)
      .then((r) => setData(r.data))
      .catch(() => setData({ overall: "operational", active: [], upcoming: [], history: [], incidents_last_7d: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;
  const cfg = OVERALL_LABEL[data?.overall] || OVERALL_LABEL.operational;
  const Icon = cfg.Icon;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 space-y-8" data-testid="status-page">
      <div>
        <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100">← Prenotoo</Link>
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3">Stato del sistema</h1>
        <p className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">Aggiornamento in tempo reale di disservizi, manutenzioni e incidenti.</p>
      </div>

      <div className={`rounded-2xl ${cfg.bg} ring-1 ${cfg.ring} p-6 flex items-start gap-4`} data-testid="status-overall">
        <Icon className={`h-7 w-7 ${cfg.color} shrink-0 mt-0.5`} />
        <div className="flex-1">
          <div className={`font-display text-xl font-medium ${cfg.color}`}>{cfg.label}</div>
          <div className="text-xs text-zinc-600 mt-1 inline-flex items-center gap-1.5 dark:text-zinc-400">
            <Server className="h-3 w-3" />
            {data?.infrastructure || "Frankfurt (DE) · Vercel · Render · MongoDB Atlas"}
          </div>
          <div className="text-[11px] text-zinc-500 mt-2 dark:text-zinc-400">
            Ultimi 7 giorni: <strong>{data?.incidents_last_7d ?? 0}</strong> {data?.incidents_last_7d === 1 ? "incidente" : "incidenti"}
          </div>
        </div>
      </div>

      <Section title="Avvisi attivi">
        {(!data?.active || data.active.length === 0) ? (
          <Empty msg="Nessun avviso attivo." />
        ) : data.active.map((n) => (
          <NoticeRow key={n.id} n={n} mode="active" />
        ))}
      </Section>

      <Section title="Manutenzioni programmate">
        {(!data?.upcoming || data.upcoming.length === 0) ? (
          <Empty msg="Nessuna manutenzione pianificata nei prossimi 14 giorni." />
        ) : data.upcoming.map((n) => (
          <NoticeRow key={n.id} n={n} mode="upcoming" />
        ))}
      </Section>

      <Section title="Cronologia (ultimi 30 giorni)">
        {(!data?.history || data.history.length === 0) ? (
          <Empty msg="Nessun incidente registrato negli ultimi 30 giorni." />
        ) : data.history.map((n) => (
          <NoticeRow key={n.id} n={n} mode="resolved" />
        ))}
      </Section>

      <p className="text-xs text-zinc-400 text-center dark:text-zinc-500">
        Per supporto: <a href="mailto:info@assiteclecce.it" className="text-blue-600 hover:underline">info@assiteclecce.it</a>
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-lg font-medium tracking-tight mb-3">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Empty({ msg }) {
  return (
    <div className="rounded-xl ring-1 ring-zinc-100 bg-white p-4 text-sm text-zinc-500 inline-flex items-center gap-2 dark:ring-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
      <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {msg}
    </div>
  );
}

function NoticeRow({ n, mode }) {
  return (
    <div className="rounded-xl ring-1 ring-zinc-100 bg-white px-4 py-3 flex items-center gap-3 dark:ring-zinc-800 dark:bg-zinc-900" data-testid={`notice-${n.id.slice(0,8)}`}>
      <span className={`h-2 w-2 rounded-full ${SEV_DOT[n.severity] || "bg-zinc-300"} shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{n.title}</div>
        <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">
          {mode === "upcoming" && <>📅 Inizio: {fmt(n.starts_at)}{n.ends_at ? ` · Fine: ${fmt(n.ends_at)}` : ""}</>}
          {mode === "active" && <>In corso da {fmt(n.starts_at)}{n.ends_at ? ` · Fine prevista: ${fmt(n.ends_at)}` : ""}</>}
          {mode === "resolved" && <>Risolto il {fmt(n.updated_at || n.ends_at)}</>}
        </div>
        {mode === "active" && n.message && <div className="text-xs text-zinc-600 mt-1 line-clamp-2 whitespace-pre-line dark:text-zinc-400">{n.message}</div>}
      </div>
      <ChevronRight className="h-4 w-4 text-zinc-300 hidden md:inline" />
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
